<script>
  /**
   * One thing, as it appears in a list.
   *
   * Built for the sparse case first: a thing may be nothing but a URL, or nothing but a line of
   * text. Nothing here assumes a title, an image or a price exists — see guides/DYNAMODB.md.
   *
   * Tapping anywhere on the card opens the thing's sheet — on every screen, with no exceptions.
   * The link and the location show here only as plain text saying the thing has them; opening
   * either is a full-size button in the sheet. A small link inside a big tap target is a mis-tap
   * waiting to happen, and one rule is easier to learn than one per screen.
   *
   * The tap target is a button stretched behind the content rather than a button wrapping it,
   * because a button may only hold phrasing content and the card holds paragraphs.
   */
  import { openEdit } from "$lib/editing.svelte.js";
  import { detailsState } from "$lib/things.svelte.js";
  import SparkleMark from "./SparkleMark.svelte";
  import { ensurePhotoAccess, photoUrl } from "$lib/photos.svelte.js";
  import { getFormatters, getTranslations } from "$lib/utils/locale.svelte.js";
  import messages from "$lib/messages/things.js";

  /**
   * `feed` leads with the photo, full width — the browsing lists (My Things, Their Things).
   * `row` is the compact Home row: a square thumbnail in the left third, and for a thing with no
   * photo an empty frame in the same place, which keeps rows aligned and invites adding one.
   *
   * Someone else's thing, on Their Things, opens its own sheet, read only (`onOpen`), and comes
   * with its photo already signed for its one URL (`photoUrl`), which `onPhotoError` renews.
   *
   * `strip` is a line along the bottom of the words saying where the thing sits: its lists on My
   * Things, whose it is and which list on Their Things.
   *
   * @type {{
   *   thing: any,
   *   variant?: "feed" | "row",
   *   onOpen?: () => void,
   *   onPhotoError?: () => void,
   *   strip?: import("svelte").Snippet,
   * }}
   */
  let { thing, variant = "feed", onOpen, onPhotoError, strip } = $props();

  const t = $derived(getTranslations(messages));

  // A thing captured as a bare link has no title yet. Showing the host is friendlier than
  // showing nothing, and far friendlier than showing the whole URL.
  const host = $derived.by(() => {
    if (!thing.url) return "";
    try {
      return new URL(thing.url).hostname.replace(/^www\./, "");
    } catch {
      return "";
    }
  });

  const heading = $derived(thing.title || host || t("untitled"));

  // Roughly, because it is: whole units once it is over a hundred ("about $780", not "$779.99").
  const priceText = $derived.by(() => {
    if (!thing.price) return "";
    const { money, moneyInt } = getFormatters(thing.price.currency);
    return t("price_about", { price: (thing.price.amount >= 100 ? moneyInt : money).format(thing.price.amount) });
  });

  // The shop by name when the page gave one, else the link's host.
  const source = $derived(thing.store || host);

  // Where getting details from the page stands. Only two states are said on the card: still
  // getting them, and none to be had — said once, quietly; the thing is fine as it is.
  const details = $derived(detailsState(thing));

  // The on-device copy while it is fresh — it is what was just captured, and it is there before
  // the upload finishes — and the signed copy otherwise.
  const image = $derived(thing.localImage || thing.photoUrl || photoUrl(thing.imageKey));
  const hasPhoto = $derived(Boolean(thing.localImage || thing.photoUrl || thing.imageKey));
</script>

<!-- The photo, when there is one, leads — full width, the way a photo feed shows it — and the
     words sit underneath. A thing without one stays a compact text card. -->
{#snippet photo()}
  <!-- alt is empty because the heading already names the thing. A load failure is most often a
       lapsed signature or a rotated key, so it asks for a fresh one; photoUrl then changes and the
       image retries by itself. -->
  {#if image}
    <img
      src={image}
      alt=""
      loading="lazy"
      onerror={() => (onPhotoError ? onPhotoError() : ensurePhotoAccess({ force: true }))}
      class="block h-full w-full object-cover {thing.justArrived ? 'arrive-fade' : ''}" />
  {/if}
{/snippet}

{#snippet words()}
  <!-- pointer-events-none on the wrapper lets taps on the text reach the button underneath. -->
  <div class="min-w-0 flex-1">
    <p class="text-ink font-semibold break-words">{heading}</p>

    {#if priceText || thing.url || thing.store}
      <!-- Collapsed whitespace between the parts is what spaces them: "about $780 · Drone Nerds". -->
      <p class="text-ink-muted mt-1 truncate text-sm">
        {#if priceText}
          <span class="text-ink font-semibold">{priceText}</span>
        {/if}
        {#if priceText && (source || thing.url)}
          <span aria-hidden="true">·</span>
        {/if}
        {source || thing.url}
      </p>
    {/if}
    <!-- A card still saving or waiting to send says that instead, beside the words. -->
    {#if !thing.pending && !thing.queued}
      {#if details === "pending"}
        <p class="text-ink-muted mt-0.5 text-xs">{t("getting_details")}</p>
      {:else if details === "nothing"}
        <p class="text-ink-muted/70 mt-0.5 text-xs">{t("no_details")}</p>
      {/if}
    {/if}

    <!-- Where it was seen, when the owner opted in. -->
    {#if thing.location}
      <p class="text-ink-muted mt-1 flex items-center gap-1 text-sm">
        <svg
          class="h-4 w-4 shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true">
          <path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z" />
          <circle cx="12" cy="10" r="2.3" />
        </svg>
        {t("location_saved")}
      </p>
    {/if}

    {#if thing.note}
      <p class="text-ink-muted mt-1 text-sm break-words {variant === 'row' ? 'line-clamp-2' : ''}">{thing.note}</p>
    {/if}

    {@render strip?.()}
  </div>

  {#if thing.pending}
    <span class="text-ink-muted shrink-0 text-xs">{t("saving")}</span>
  {:else if thing.queued}
    <!-- Safe on the phone, not yet on the server. Full strength rather than dimmed: nothing is
         wrong, and it goes by itself once there is signal. -->
    <span class="text-amber-deep flex shrink-0 items-center gap-1 text-xs font-semibold">
      <svg
        class="h-3.5 w-3.5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true">
        <path d="M12 20V10" />
        <path d="m8 14 4-4 4 4" />
        <path d="M5 5h14" />
      </svg>
      {t("waiting_to_send")}
    </span>
  {/if}
{/snippet}

<!-- data-thing-card: how the capture sheet finds the newest card to bring into view; the scroll
     margin keeps it clear of the sticky header, and the status bar above it, when it does. -->
<article
  data-thing-card
  class="card relative overflow-hidden {thing.pending ? 'opacity-60' : ''}"
  style="scroll-margin-top: calc(env(safe-area-inset-top) + 5rem);">
  <!-- The "ting": when details land from the page, the photo fades in and the app's own mark passes
       over the card once. Only on arrival (the store sets justArrived for a moment), never on a
       plain render. Decorative — the new details themselves are what a screen reader gets. -->
  {#if thing.justArrived}
    <span class="arrive-sparkle text-amber pointer-events-none absolute top-3 right-3 z-10">
      <SparkleMark size={32} />
    </span>
  {/if}

  <!-- A thing still saving or waiting to send is not on the server yet, so there is nothing to
       edit until it lands. -->
  {#if !thing.pending && !thing.queued}
    <button
      type="button"
      onclick={() => (onOpen ? onOpen() : openEdit(thing.thingId))}
      class="hover:bg-surface-sunk/60 absolute inset-0 transition"
      aria-label={onOpen ? t("open_thing", { name: heading }) : t("edit_thing", { name: heading })}>
    </button>
  {/if}

  {#if variant === "row"}
    <div class="pointer-events-none relative flex items-start gap-3 p-3">
      <!-- On your own things always drawn, photo or not: rows stay aligned, and an empty frame
           asks to be filled. Someone else's (`onOpen`) is not yours to fill: no photo, no frame. -->
      {#if hasPhoto || !onOpen}
        <div
          class="bg-surface-sunk text-ink-muted flex aspect-square w-1/3 shrink-0 items-center justify-center overflow-hidden rounded-xl">
          {#if hasPhoto}
            {@render photo()}
          {:else}
            <svg
              class="h-7 w-7 opacity-50"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true">
              <path
                d="M4 8.5A1.5 1.5 0 0 1 5.5 7h2l1.4-2h6.2l1.4 2h2A1.5 1.5 0 0 1 20 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5z" />
              <circle cx="12" cy="13" r="3.5" />
            </svg>
          {/if}
        </div>
      {/if}
      {@render words()}
    </div>
  {:else}
    {#if hasPhoto}
      <!-- The frame is drawn as soon as the thing has a photo, so the list does not jump when the
           signature arrives and the image with it. Positioned so it paints above the stretched
           button; pointer-events-none so a tap on it still opens the thing. -->
      <div class="bg-surface-sunk pointer-events-none relative aspect-[4/5] w-full">
        {@render photo()}
      </div>
    {/if}

    <div class="pointer-events-none relative flex items-start gap-3 p-4">
      {@render words()}
    </div>
  {/if}
</article>

<style>
  .arrive-fade {
    animation: arrive-fade 500ms ease-out;
  }

  .arrive-sparkle {
    animation: arrive-sparkle 1.2s ease-out forwards;
  }

  @keyframes arrive-fade {
    from {
      opacity: 0;
    }
  }

  @keyframes arrive-sparkle {
    0% {
      opacity: 0;
      transform: scale(0.4) rotate(-20deg);
    }
    35% {
      opacity: 1;
      transform: scale(1.15) rotate(0deg);
    }
    100% {
      opacity: 0;
      transform: scale(0.9) rotate(10deg);
    }
  }
</style>
